import moment from "moment-timezone";

import {SAFELISTING_COVERAGE_FORMATS as F} from "@/src/modules/core/lib/formatters";
import {isFullDayRange} from "@/src/modules/properties/lib/citation-range-hours";
import type {PropertyRuleTimeRange} from "@/src/modules/properties/types";

export type SafelistingCoverageWindowKind = "full_day" | "overnight" | "daytime";

export type SafelistingCoverageNightRow = {
  anchorDate: string;
  startLabel: string;
  endLabel: string;
  rangeLabel: string;
  /** Short date for dense lists, e.g. "Jun 17". */
  compactDateLabel: string;
  /** One-line window without repeating the anchor date, e.g. "22:00 → Jun 18, 08:00". */
  compactTimeLabel: string;
  /** Clock-time pattern repeated each night, e.g. "22:00 → 08:00". */
  timePatternLabel: string;
};

export type SafelistingCoveragePreview = {
  windowKind: SafelistingCoverageWindowKind;
  allowedHoursLine: string;
  windowHint: string;
  dateFieldLabel: string;
  dateWord: string;
  introLine: string;
  overallRangeLabel: string | null;
  /** Shorter overall line for the preview header. */
  overallCompactLabel: string | null;
  /** Set when every row shares the same clock-time pattern (typical overnight rules). */
  sharedTimePattern: string | null;
  rows: SafelistingCoverageNightRow[];
  permitCount: number;
};

function minutesFromHHMM(hhmm: string): number {
  const [h, m] = hhmm.split(":").map((part) => parseInt(part, 10));
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

export function getSafelistingCoverageWindowKind(
  range: PropertyRuleTimeRange,
): SafelistingCoverageWindowKind {
  if (isFullDayRange(range)) return "full_day";
  if (minutesFromHHMM(range.end) <= minutesFromHHMM(range.start)) return "overnight";
  return "daytime";
}

function formatHHMM(hhmm: string): string {
  return moment(hhmm, "HH:mm").format(F.time);
}

function anchorDateTime(anchor: string, hhmm: string, timeZone: string) {
  return moment.tz(`${anchor} ${hhmm}`, "YYYY-MM-DD HH:mm", timeZone);
}

function buildAllowedHoursLine(
  range: PropertyRuleTimeRange,
  kind: SafelistingCoverageWindowKind,
): string {
  if (kind === "full_day") {
    return "24 hours on each selected date.";
  }
  const start = formatHHMM(range.start);
  const end = formatHHMM(range.end);
  if (kind === "overnight") {
    return `${start} – ${end} each night (ends the next morning).`;
  }
  return `${start} – ${end} on each selected date.`;
}

function buildWindowHint(_kind: SafelistingCoverageWindowKind): string {
  // Overnight end-time is already in `allowedHoursLine` on the field label.
  return "";
}

export function getSafelistingCoverageFormCopy(
  range: PropertyRuleTimeRange,
): Pick<
  SafelistingCoveragePreview,
  "windowKind" | "allowedHoursLine" | "windowHint" | "dateFieldLabel" | "dateWord" | "introLine"
> {
  const windowKind = getSafelistingCoverageWindowKind(range);
  const dateWord = windowKind === "overnight" ? "night" : "date";
  return {
    windowKind,
    allowedHoursLine: buildAllowedHoursLine(range, windowKind),
    windowHint: buildWindowHint(windowKind),
    dateFieldLabel: windowKind === "overnight" ? "Nights (from – to)" : "Dates (from – to)",
    dateWord,
    introLine: `Select ${dateWord}s to cover. Coverage is shown below.`,
  };
}

function buildNightRow(
  anchor: string,
  range: PropertyRuleTimeRange,
  kind: SafelistingCoverageWindowKind,
  timeZone: string,
): SafelistingCoverageNightRow {
  if (kind === "full_day") {
    const startM = moment.tz(anchor, "YYYY-MM-DD", timeZone).startOf("day");
    const endM = moment.tz(anchor, "YYYY-MM-DD", timeZone).endOf("day");
    const startLabel = startM.format(F.full);
    const endLabel = endM.format(F.full);
    return {
      anchorDate: anchor,
      startLabel,
      endLabel,
      rangeLabel: `${startLabel} → ${endLabel}`,
      compactDateLabel: startM.format(F.compactDate),
      compactTimeLabel: "All day",
      timePatternLabel: "All day",
    };
  }

  if (kind === "overnight") {
    const startM = anchorDateTime(anchor, range.start, timeZone);
    const endAnchor = moment.tz(anchor, "YYYY-MM-DD", timeZone).add(1, "day").format("YYYY-MM-DD");
    const endM = anchorDateTime(endAnchor, range.end, timeZone);
    const startLabel = startM.format(F.full);
    const endLabel = endM.format(F.full);
    return {
      anchorDate: anchor,
      startLabel,
      endLabel,
      rangeLabel: `${startLabel} → ${endLabel}`,
      compactDateLabel: startM.format(F.compactDate),
      compactTimeLabel: `${startM.format(F.time)} → ${endM.format(F.compactWithDate)}`,
      timePatternLabel: `${startM.format(F.time)} → ${endM.format(F.time)}`,
    };
  }

  const startM = anchorDateTime(anchor, range.start, timeZone);
  const endM = anchorDateTime(anchor, range.end, timeZone);
  const startLabel = startM.format(F.full);
  const endLabel = endM.format(F.full);
  return {
    anchorDate: anchor,
    startLabel,
    endLabel,
    rangeLabel: `${startLabel} → ${endLabel}`,
    compactDateLabel: startM.format(F.compactDate),
    compactTimeLabel: `${startM.format(F.time)} → ${endM.format(F.time)}`,
    timePatternLabel: `${startM.format(F.time)} → ${endM.format(F.time)}`,
  };
}

function buildOverallCompactLabel(
  rows: SafelistingCoverageNightRow[],
  kind: SafelistingCoverageWindowKind,
): string | null {
  if (rows.length === 0) return null;
  const first = rows[0]!;
  const last = rows[rows.length - 1]!;

  if (rows.length === 1) {
    return `${first.compactDateLabel}, ${first.compactTimeLabel}`;
  }

  if (kind === "overnight") {
    const startTime = first.compactTimeLabel.split(" → ")[0] ?? first.compactTimeLabel;
    const endTime = last.compactTimeLabel.split(" → ").pop() ?? last.compactTimeLabel;
    return `${first.compactDateLabel}, ${startTime} → ${endTime}`;
  }

  if (kind === "full_day") {
    return `${first.compactDateLabel} – ${last.compactDateLabel} (all day)`;
  }

  return `${first.compactDateLabel} – ${last.compactDateLabel}`;
}

/** Coverage end instant for one anchor date (property timezone + rule hours). */
export function getSafelistingAnchorCoverageEnd(
  anchorDate: string,
  rangeHours: PropertyRuleTimeRange,
  timeZone: string,
): moment.Moment {
  const kind = getSafelistingCoverageWindowKind(rangeHours);
  if (kind === "full_day") {
    return moment.tz(anchorDate, "YYYY-MM-DD", timeZone).endOf("day");
  }
  if (kind === "overnight") {
    const endAnchor = moment
      .tz(anchorDate, "YYYY-MM-DD", timeZone)
      .add(1, "day")
      .format("YYYY-MM-DD");
    return anchorDateTime(endAnchor, rangeHours.end, timeZone);
  }
  return anchorDateTime(anchorDate, rangeHours.end, timeZone);
}

/** True when the anchor's coverage window has not yet ended (still selectable for new permits). */
export function isSafelistingAnchorDateSelectable(
  anchorDate: string,
  rangeHours: PropertyRuleTimeRange,
  timeZone: string,
  now: moment.Moment = moment.tz(timeZone),
): boolean {
  return getSafelistingAnchorCoverageEnd(anchorDate, rangeHours, timeZone).isAfter(now);
}

/** True once this permit night's coverage window has begun (property timezone + rule hours). */
export function hasSafelistingNightCoverageStarted(
  anchorDate: string,
  rangeHours: PropertyRuleTimeRange,
  timeZone: string,
  now: moment.Moment = moment.tz(timeZone),
): boolean {
  const kind = getSafelistingCoverageWindowKind(rangeHours);
  if (kind === "full_day") {
    return now.isSameOrAfter(moment.tz(anchorDate, "YYYY-MM-DD", timeZone).startOf("day"));
  }
  const startM = anchorDateTime(anchorDate, rangeHours.start, timeZone);
  return now.isSameOrAfter(startM);
}

export function computeSafelistingCoveragePreview(
  rangeHours: PropertyRuleTimeRange,
  days: string[],
  timeZone?: string,
): SafelistingCoveragePreview | null {
  if (days.length === 0) return null;

  const tz = timeZone?.trim() || moment.tz.guess();
  const formCopy = getSafelistingCoverageFormCopy(rangeHours);
  const rows = days.map((anchor) => buildNightRow(anchor, rangeHours, formCopy.windowKind, tz));
  const first = rows[0]!;
  const last = rows[rows.length - 1]!;

  const overallCompactLabel = buildOverallCompactLabel(rows, formCopy.windowKind);
  const sharedTimePattern =
    rows.length > 1 && rows.every((row) => row.timePatternLabel === rows[0]?.timePatternLabel)
      ? (rows[0]?.timePatternLabel ?? null)
      : null;

  return {
    ...formCopy,
    overallRangeLabel: `${first.startLabel} → ${last.endLabel}`,
    overallCompactLabel,
    sharedTimePattern,
    rows,
    permitCount: days.length,
  };
}
